import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { DocumotoHotpointDto } from '../../api/documoto/types';
import { bomPartFragmentId } from './documoto-bom-helpers';

export type HotspotActivatePayload = {
	partId?: string | number;
	itemNumber?: string | number;
};

type DiagramHp = {
	key: string;
	partId?: string | number | null;
	itemNumber?: string | number | null;
	left: number;
	top: number;
	width: number;
	height: number;
	label?: string | null;
	coordMode: 'fraction' | 'pixel';
};

function mapDto(h: DocumotoHotpointDto): DiagramHp {
	const mode = String(h.coordMode || 'pixel').toLowerCase() === 'fraction' ? 'fraction' : 'pixel';
	return {
		key: h.key,
		partId: h.partEntityId ?? undefined,
		itemNumber: h.itemNumber ?? undefined,
		left: h.left,
		top: h.top,
		width: h.width,
		height: h.height,
		label: h.label ?? undefined,
		coordMode: mode,
	};
}

function hotpointStyle(hp: DiagramHp, natW: number, natH: number): CSSProperties {
	if (hp.coordMode === 'fraction') {
		return {
			left: `${hp.left * 100}%`,
			top: `${hp.top * 100}%`,
			width: `${hp.width * 100}%`,
			height: `${hp.height * 100}%`,
		};
	}
	const w = natW > 0 ? natW : 1;
	const h = natH > 0 ? natH : 1;
	return {
		left: `${(hp.left / w) * 100}%`,
		top: `${(hp.top / h) * 100}%`,
		width: `${(hp.width / w) * 100}%`,
		height: `${(hp.height / h) * 100}%`,
	};
}

function hotspotLinkHref(hp: DiagramHp, itemNumberToPartId?: ReadonlyMap<string, string>): string {
	if (hp.partId !== undefined && hp.partId !== null) {
		return `#${bomPartFragmentId(hp.partId)}`;
	}
	if (hp.itemNumber !== undefined && hp.itemNumber !== null) {
		const key = typeof hp.itemNumber === 'string' ? hp.itemNumber.trim() : String(hp.itemNumber);
		if (!key) {
			return '#';
		}
		const resolved = itemNumberToPartId?.get(key);
		if (resolved) {
			return `#${bomPartFragmentId(resolved)}`;
		}
	}
	return '#';
}

type Props = {
	diagramImageDataUrl: string | null;
	hotpoints: DocumotoHotpointDto[];
	itemNumberToPartId?: ReadonlyMap<string, string>;
	onHotspotActivate?: (payload: HotspotActivatePayload) => void;
};

export function DocumotoPageDiagramPanel({
	diagramImageDataUrl,
	hotpoints,
	itemNumberToPartId,
	onHotspotActivate,
}: Props) {
	const [nat, setNat] = useState({ w: 1, h: 1 });
	const mapped = hotpoints.map(mapDto);
	const hasPixelCoords = mapped.some((h) => h.coordMode === 'pixel');
	/* Pixel coords are converted using natural size; 1×1 placeholder breaks layout until onLoad. Fraction coords ignore nat. */
	const natReady = !hasPixelCoords || (nat.w > 1 && nat.h > 1);

	if (!diagramImageDataUrl) {
		return <div className="diagram-panel diagram-panel--empty">No illustration available for this page.</div>;
	}

	return (
		<div className="diagram-panel">
			<div className="diagram-frame">
				<img
					className="diagram-img"
					src={diagramImageDataUrl}
					alt="Parts diagram"
					onLoad={(e) => {
						const im = e.currentTarget;
						setNat({
							w: im.naturalWidth || 1,
							h: im.naturalHeight || 1,
						});
					}}
				/>
				{natReady
					? mapped.map((hp) => {
							const href = hotspotLinkHref(hp, itemNumberToPartId);
							const title =
								hp.label ??
								(hp.itemNumber != null
									? `Item ${String(hp.itemNumber)}`
									: hp.partId != null
										? `Part ${String(hp.partId)}`
										: 'Hotspot');
							return (
								<a
									key={hp.key}
									href={href}
									className="diagram-hotspot-link"
									style={hotpointStyle(hp, nat.w, nat.h)}
									title={title}
									aria-label={title}
									onClick={(e) => {
										e.preventDefault();
										onHotspotActivate?.({
											partId: hp.partId ?? undefined,
											itemNumber: hp.itemNumber ?? undefined,
										});
									}}
								>
									<span className="diagram-hotspot-label"> </span>
								</a>
							);
						})
					: null}
			</div>
		</div>
	);
}
